import React, { ReactNode, useState, memo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { RootState } from "../store";
import { logout, setUser } from "../store/slices/authSlice";
import { closeFormSidebar } from "../store/slices/uiSlice";
import RequisitesSidebar from "./RequisitesSidebar";
import ThemeToggle from "./ThemeToggle";

import {
  useGetBalanceQuery,
  useGetExchangeRateQuery,
  useGetProfileQuery,
  useToggleMyStatusMutation,
  useGetAppealsQuery,
  useGetOrdersQuery,
} from "../store/trader-api";
import { useExchangeRateSocket } from "../hooks/useExchangeRateSocket";
import { useTraderNotifications } from "../hooks/useTraderNotifications";
import { useTheme } from "../hooks/useTheme";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: var(--gradient-bg);
  position: relative;
`;

const Sidebar = styled.aside<{
  isOpen: boolean;
  isMobile: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 280px;
  background: none;
  color: white;
  overflow: visible;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 200;
  transform: translateX(
    ${(props) => (props.isMobile ? (props.isOpen ? "0" : "-100%") : "0")}
  );
  pointer-events: ${(props) =>
    props.isMobile ? (props.isOpen ? "auto" : "none") : "auto"};

  @media (max-width: 768px) {
    display: none;
  }
`;

const SidebarContent = styled.div`
  width: 280px;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
`;

const NavigationContainer = styled.div<{ formSidebarOpen: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(${(props) => (props.formSidebarOpen ? "-100%" : "0")});
`;

const Navigation = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 2rem;
`;

const NavItem = styled(Link)<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  width: 216px;
  padding: 1rem 1.5rem;
  justify-content: flex-start;
  color: ${(props) => (props.isActive ? "var(--nav-active-text)" : "var(--nav-text)")};
  background: ${(props) =>
    props.isActive ? "var(--nav-active-bg)" : "var(--nav-bg)"};
  backdrop-filter: blur(12px);
  border-radius: 1rem;
  text-decoration: none;
  gap: 1rem;
  font-weight: ${(props) => (props.isActive ? "700" : "600")};
  font-size: 0.875rem;
  border: 1px solid
    ${(props) =>
      props.isActive ? "var(--nav-active-border)" : "var(--nav-border)"};
  box-shadow: var(--shadow-card);
  overflow: hidden;
`;

const NavLabel = styled.div<{ isActive: boolean }>`
  overflow: hidden;
  white-space: nowrap;
  flex: 1;
`;

const NavIcon = styled.div<{ isActive?: boolean }>`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const MainContent = styled.main<{ sidebarOpen: boolean; isMobile: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 100vh;
  margin-left: ${(props) =>
    !props.isMobile && props.sidebarOpen ? "280px" : "0"};
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    margin-left: 0;
    padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
  }
`;

const Header = styled.header`
  background: transparent;
  padding: 0 2rem;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0 0.75rem;
    height: 56px;
    gap: 0.5rem;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const MobileHeaderLogo = styled.img`
  display: none;
  height: 22px;
  width: auto;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 2rem 1rem 2rem;
  margin-bottom: 1rem;
`;

const Logo = styled.img`
  height: 28px;
  width: auto;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    height: 24px;
  }
`;

const HeaderTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.025em;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
    flex: 1;
    justify-content: flex-end;
  }
`;

const BalanceSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem;
  background: var(--bg-glass);
  border-radius: 1rem;
  border: 1px solid var(--border-light);
  backdrop-filter: blur(12px);

  @media (max-width: 768px) {
    display: none;
  }
`;

const ExchangeRateSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 1rem;
  background: var(--bg-glass);
  border-radius: 0.75rem;
  border: 1px solid var(--border-light);
  backdrop-filter: blur(12px);
  height: 56px;

  @media (max-width: 768px) {
    padding: 0.25rem 0.5rem;
    gap: 0.25rem;
    height: 36px;
    border-radius: 0.5rem;
  }
`;

const ExchangeRateDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 0.25rem;
  }
`;

const ExchangeRateAmount = styled.div`
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const ExchangeRateLabel = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 0.5625rem;

    .rate-label-prefix {
      display: none;
    }
  }
`;

const BalanceDisplay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const BalanceAmount = styled.div`
  font-size: 1.125rem;
  font-weight: 800;
  color: var(--text-primary);
  line-height: 1.2;
`;

const BalanceLabel = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const WithdrawButton = styled.button`
  background: var(--gradient-withdraw);
  color: #ffffff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: var(--gradient-withdraw-hover);
    transform: translateY(-1px);
    box-shadow: var(--shadow-withdraw);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.6875rem;
  }
`;

const UserMenu = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: var(--bg-glass);
  border-radius: 0.75rem;
  border: 1px solid var(--border-light);
  transition: all 0.3s ease;
  height: 56px;

  &:hover {
    background: var(--bg-card);
    box-shadow: var(--shadow-card);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserAvatar = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  background: var(--gradient-avatar);
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 700;
  font-size: 1rem;
  box-shadow: var(--shadow-avatar);

  @media (max-width: 768px) {
    width: 2rem;
    height: 2rem;
    font-size: 0.875rem;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
`;

const UserRole = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
  line-height: 1.2;
`;

const OnlineStatusToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: var(--bg-glass);
  border-radius: 0.75rem;
  border: 1px solid var(--border-light);
  transition: all 0.3s ease;
  cursor: pointer;
  user-select: none;
  box-shadow: var(--shadow-sm);
  width: 150px;
  height: 56px;

  &:hover {
    background: var(--bg-card);
    box-shadow: var(--shadow-md);
    border-color: var(--accent-primary);
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 0.25rem 0.5rem;
    gap: 0.375rem;
    width: auto;
    min-width: 110px;
    height: 36px;
    border-radius: 0.5rem;
    display: none;
  }
`;

const StatusIndicator = styled.div<{ isActive: boolean }>`
  width: 2rem;
  height: 1rem;
  border-radius: 0.5rem;
  background: ${(props) => (props.isActive ? "var(--status-active)" : "var(--status-inactive)")};
  position: relative;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &::after {
    content: "";
    position: absolute;
    top: 0.125rem;
    left: ${(props) => (props.isActive ? "1.125rem" : "0.125rem")};
    width: 0.75rem;
    height: 0.75rem;
    background: #ffffff;
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: var(--shadow-sm);
  }

  @media (max-width: 768px) {
    width: 1.625rem;
    height: 0.875rem;

    &::after {
      top: 0.0938rem;
      left: ${(props) => (props.isActive ? "0.875rem" : "0.0938rem")};
      width: 0.6875rem;
      height: 0.6875rem;
    }
  }
`;

const StatusText = styled.span<{ isActive: boolean }>`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(props) => (props.isActive ? "var(--accent-primary)" : "var(--text-secondary)")};
  transition: all 0.3s ease;
  white-space: nowrap;
  flex: 1;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 0.6875rem;
    flex: none;
  }
`;

const LogoutButton = styled.button`
  background: var(--bg-hover);
  border: 1px solid var(--border-light);
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 0.875rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;

  &:hover {
    background: var(--alert-error-bg);
    color: var(--accent-error);
    border-color: var(--alert-error-border);
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

/* ====== Mobile Bottom Navigation ====== */
const MobileBottomNav = styled.nav`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    height: 64px;
    background: var(--bg-glass);
    backdrop-filter: blur(20px);
    border-top: 1px solid var(--border-light);
    z-index: 300;
    padding: 0 0.25rem;
    padding-bottom: env(safe-area-inset-bottom, 0px);
    align-items: center;
    justify-content: space-around;
  }
`;

const MobileNavItem = styled(Link)<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  padding: 0.375rem 0.25rem;
  text-decoration: none;
  color: ${(props) => (props.isActive ? "var(--accent-primary)" : "var(--text-muted)")};
  font-size: 0.625rem;
  font-weight: ${(props) => (props.isActive ? "700" : "500")};
  position: relative;
  min-width: 48px;
  transition: color 0.2s;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.92);
  }
`;

const MobileNavIcon = styled.div<{ isActive?: boolean }>`
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    ${(props) => !props.isActive ? `filter: brightness(0) saturate(100%) invert(70%) sepia(6%) saturate(517%) hue-rotate(182deg) brightness(92%) contrast(88%);` : ``}
  }
`;

const MobileNavBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  font-size: 0.5625rem;
  font-weight: 700;
  color: #ffffff;
  background: var(--accent-error);
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MobileNavLabel = styled.span`
  line-height: 1;
  white-space: nowrap;
`;

/* ====== Mobile Hamburger Menu ====== */
const HamburgerButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: var(--bg-glass);
    border: 1px solid var(--border-light);
    border-radius: 0.5rem;
    cursor: pointer;
    color: var(--text-secondary);
    font-size: 1.25rem;
    -webkit-tap-highlight-color: transparent;
    transition: all 0.2s;
    flex-shrink: 0;

    &:active {
      background: var(--bg-secondary);
    }
  }
`;

const MobileMenuOverlay = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.isOpen ? "block" : "none")};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--bg-modal-overlay);
    z-index: 400;
  }
`;

const MobileMenuDrawer = styled.div<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 280px;
    max-width: 80vw;
    background: var(--bg-card);
    z-index: 500;
    transform: translateX(${(props) => (props.isOpen ? "0" : "100%")});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: var(--shadow-lg);
    padding: 1.5rem 1rem;
    gap: 0.5rem;
    overflow-y: auto;
  }
`;

const MobileMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 0.5rem;
`;

const MobileMenuUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const MobileMenuCloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.25rem;
`;

const MobileMenuItem = styled.button<{ variant?: 'danger' }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: ${(props) => props.variant === 'danger' ? 'var(--alert-error-bg)' : 'var(--bg-primary)'};
  border: 1px solid ${(props) => props.variant === 'danger' ? 'var(--alert-error-border)' : 'var(--border-color)'};
  border-radius: 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.variant === 'danger' ? 'var(--alert-error-text)' : 'var(--text-primary)'};
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: all 0.2s;

  &:active {
    transform: scale(0.98);
    background: ${(props) => props.variant === 'danger' ? 'var(--accent-error-light)' : 'var(--border-color)'};
  }
`;

const MobileMenuDivider = styled.div`
  height: 1px;
  background: var(--border-color);
  margin: 0.5rem 0;
`;

const ContentArea = styled.div`
  flex: 1;
  overflow-y: auto;
  background: var(--gradient-bg);

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const Overlay = styled.div<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? "block" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  backdrop-filter: blur(4px);
  z-index: 150;
  transition: all 0.3s ease;

  @media (min-width: 769px) {
    display: none;
  }
`;

// Модальное окно вывода средств
const WithdrawModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-modal-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const WithdrawModalContent = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 2rem;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const WithdrawModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const WithdrawModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const WithdrawCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-muted);

  &:hover {
    color: var(--text-primary);
  }
`;

const WithdrawFormGroup = styled.div`
  margin-bottom: 1rem;
`;

const WithdrawLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const WithdrawInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--accent-primary);
    box-shadow: var(--focus-ring);
  }

  &:invalid {
    border-color: var(--accent-error);
  }
`;

const WithdrawButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const WithdrawFormButton = styled.button<{ variant?: "primary" | "secondary" }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${({ variant }) =>
    variant === "primary"
      ? `
    background: var(--accent-primary);
    color: #ffffff;
    border: none;

    &:hover {
      background: var(--accent-primary-hover);
    }
  `
      : `
    background: var(--bg-card);
    color: var(--text-primary);
    border: 1px solid var(--border-input);

    &:hover {
      background: var(--bg-primary);
    }
  `}

  &:disabled {
    background: var(--text-muted);
    cursor: not-allowed;
  }
`;

const WithdrawErrorText = styled.div`
  color: var(--accent-error);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const WithdrawSuccessText = styled.div`
  color: var(--accent-success);
  font-weight: 500;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: center;
`;

const WalletInfoCard = styled.div<{ isActive: boolean }>`
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-top: 0.5rem;
`;

const WalletAddress = styled.div`
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.8125rem;
  background: var(--bg-primary);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--border-color);
  margin: 0.5rem 0;
  word-break: break-all;
  cursor: pointer;
  color: var(--text-primary);
  transition: all 0.15s;
  user-select: all;

  &:hover {
    border-color: var(--accent-primary);
    background: var(--accent-primary-light);
  }
`;

const TimeLeft = styled.div<{ isUrgent: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => (props.isUrgent ? "var(--accent-error)" : "var(--text-secondary)")};
`;

const Alert = styled.div<{ type: "info" | "warning" | "error" | "success" }>`
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  background: ${(props) => {
    switch (props.type) {
      case "info":
        return "var(--alert-info-bg)";
      case "warning":
        return "var(--alert-warning-bg)";
      case "error":
        return "var(--alert-error-bg)";
      case "success":
        return "var(--alert-success-bg)";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.type) {
        case "info":
          return "var(--alert-info-border)";
        case "warning":
          return "var(--alert-warning-border)";
        case "error":
          return "var(--alert-error-border)";
        case "success":
          return "var(--alert-success-border)";
      }
    }};
  color: ${(props) => {
    switch (props.type) {
      case "info":
        return "var(--alert-info-text)";
      case "warning":
        return "var(--alert-warning-text)";
      case "error":
        return "var(--alert-error-text)";
      case "success":
        return "var(--alert-success-text)";
    }
  }};
`;

// Мемоизированный компонент для курса валют, чтобы избежать дерганья при re-render
const ExchangeRateComponent = memo(
  ({ exchangeRate }: { exchangeRate: any }) => (
    <ExchangeRateSection>
      <ExchangeRateDisplay>
        <ExchangeRateLabel><span className="rate-label-prefix">Курс </span>USDT/RUB</ExchangeRateLabel>
        <ExchangeRateAmount>
          {exchangeRate ? `${exchangeRate.rate.toFixed(2)} ₽` : "—"}
        </ExchangeRateAmount>
      </ExchangeRateDisplay>
    </ExchangeRateSection>
  )
);

interface LayoutProps {
  children: ReactNode;
}

// Badge component for showing count
const NavBadge = styled.span<{ isActive?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  color: ${(props) => (props.isActive ? "var(--accent-primary)" : "#ffffff")};
  background: ${(props) => (props.isActive ? "#ffffff" : "var(--accent-primary)")};
  border-radius: 9999px;
  margin-left: auto;
  box-shadow: var(--shadow-sm);
`;

const navItems = [
  {
    path: "/dashboard",
    label: "Дашборд",
    icon: "/icons/dashboard.svg",
    activeIcon: "/icons/dashboard-active.svg",
    badgeKey: null,
  },
  {
    path: "/orders",
    label: "Ордера",
    icon: "/icons/orders.svg",
    activeIcon: "/icons/orders-active.svg",
    badgeKey: "orders" as const,
  },
  {
    path: "/requisites",
    label: "Реквизиты",
    icon: "/icons/requisites.svg",
    activeIcon: "/icons/requisites-active.svg",
    badgeKey: null,
  },
  {
    path: "/appeals",
    label: "Апелляции",
    icon: "/icons/appeals.svg",
    activeIcon: "/icons/appeals-active.svg",
    badgeKey: "appeals" as const,
  },
  {
    path: "/balance",
    label: "Баланс",
    icon: "/icons/balance.svg",
    activeIcon: "/icons/balance-active.svg",
    badgeKey: null,
  },
  {
    path: "/settings",
    label: "Настройки",
    icon: "/icons/settings.svg",
    activeIcon: "/icons/settings-active.svg",
    badgeKey: null,
  },
];

function Layout({ children }: LayoutProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();

  const { user } = useSelector((state: RootState) => state.auth);
  const { formSidebarOpen, editingHolder, editingRequisite } = useSelector(
    (state: RootState) => state.ui
  );
  const { data: balance } = useGetBalanceQuery();
  const { data: profile } = useGetProfileQuery();
  const [toggleMyStatus, { isLoading: isTogglingStatus }] =
    useToggleMyStatusMutation();

  // Получаем правильный ID трейдера
  const traderId = profile?.id || user?.trader?.id;

  // WebSocket notifications for real-time counter updates
  const {
    isConnected: wsNotificationsConnected,
  } = useTraderNotifications(traderId);

  // Fallback: REST API queries for navigation badges - pending orders and appeals
  // Polling disabled when WebSocket is connected
  const { data: pendingOrdersData } = useGetOrdersQuery(
    { status: 'PENDING', page: 1, limit: 1 },
    { pollingInterval: wsNotificationsConnected ? 0 : 30000 }
  );
  const { data: pendingAppealsData } = useGetAppealsQuery(
    { status: 'PENDING', page: 1, limit: 1 },
    { pollingInterval: wsNotificationsConnected ? 0 : 30000 }
  );

  // Calculate counts for badges (format: 100+ if over 100)
  // Prefer WebSocket counts when connected, otherwise use REST API data
  const formatBadgeCount = (count: number | undefined): string | null => {
    if (!count || count === 0) return null;
    if (count > 100) return '100+';
    return count.toString();
  };

  // Use REST API data for total counts. WebSocket events will trigger RTK Query refetch
  // via invalidateTags in useTraderNotifications, keeping these counts up to date.
  const ordersCount = pendingOrdersData?.meta?.total;
  const appealsCount = pendingAppealsData?.meta?.total;

  const badgeCounts: Record<string, string | null> = {
    orders: formatBadgeCount(ordersCount),
    appeals: formatBadgeCount(appealsCount),
  };

  // Закрываем панель форм при переходе на другую страницу
  const [previousPath, setPreviousPath] = React.useState(location.pathname);

  React.useEffect(() => {
    if (location.pathname !== previousPath && formSidebarOpen) {
      dispatch(closeFormSidebar());
    }
    setPreviousPath(location.pathname);
  }, [location.pathname, previousPath, formSidebarOpen, dispatch]);

  // Получаем первоначальный курс через REST API (с обработкой ошибок)
  const { data: initialExchangeRate, error: exchangeRateError } =
    useGetExchangeRateQuery(undefined, {
      // Fail-soft: если API недоступен, не ломаем страницу
      skip: false,
      refetchOnMountOrArgChange: false,
    });

  // Используем WebSocket для получения обновлений курса в реальном времени
  const backendUrl =
    process.env.REACT_APP_API_URL?.replace("/api", "") ||
    "http://localhost:3001";
  const {
    exchangeRate: wsExchangeRate,
    isConnected: wsConnected,
    error: wsError,
  } = useExchangeRateSocket(backendUrl);

  // Используем курс из WebSocket, если доступен, иначе из REST API
  const exchangeRate = wsExchangeRate || initialExchangeRate;

  // Логирование для отладки
  React.useEffect(() => {
    console.log("WebSocket connected:", wsConnected);
    console.log("WebSocket error:", wsError);
    console.log("Exchange rate (WS):", wsExchangeRate);
    console.log("Exchange rate (REST):", initialExchangeRate);
    console.log("Final exchange rate:", exchangeRate);
  }, [wsConnected, wsError, wsExchangeRate, initialExchangeRate, exchangeRate]);

  // Логирование изменений статуса пользователя
  React.useEffect(() => {
    console.log("🔍 Пользователь изменился:", {
      user: user,
      traderStatus: user?.trader?.status,
      isActive: user?.trader?.status === "ACTIVE",
    });
  }, [user]);
  const { refetch: refetchProfile } = useGetProfileQuery();

  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositStep, setDepositStep] = useState(1); // 1 - ввод суммы, 2 - резервирование кошелька
  const [depositForm, setDepositForm] = useState({
    amount: "",
    method: "crypto", // crypto, sbp, bank_transfer
    cardNumber: "",
    phoneNumber: "",
  });
  const [reservedWallet, setReservedWallet] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isRequestingWallet, setIsRequestingWallet] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
    };

    // Инициализация
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Проверяем наличие зарезервированного кошелька при загрузке
  React.useEffect(() => {
    checkReservedWallet();
  }, []);

  // Таймер для обновления времени резервирования кошелька
  React.useEffect(() => {
    if (!reservedWallet) return;

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const reserved = new Date(reservedWallet.reservedUntil).getTime();
      const diff = Math.max(0, Math.floor((reserved - now) / 1000));

      setTimeLeft(diff);

      if (diff <= 0) {
        setReservedWallet(null);
        setDepositStep(1);
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [reservedWallet]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  const handleToggleOnlineStatus = async () => {
    try {
      console.log("🔄 Переключение статуса трейдера...");
      const result = await toggleMyStatus().unwrap();
      console.log("✅ Статус успешно переключен:", result);

      // Обновляем данные пользователя в Redux store если trader существует
      if (user?.trader) {
        dispatch(
          setUser({
            ...user,
            trader: {
              ...user.trader,
              status: result.status,
            },
          })
        );
      }

      // Принудительно обновляем профиль для синхронизации данных
      refetchProfile();
    } catch (error: any) {
      console.error("❌ Ошибка при переключении статуса:", error);
      alert(
        error?.data?.message ||
          "Не удалось переключить статус. Попробуйте снова."
      );
    }
  };

  const getUserInitials = () => {
    if (!user?.login) return "P";
    return user.login.charAt(0).toUpperCase();
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const copyToClipboard = (text: string) => {
    // Убираем жесткую проверку window.isSecureContext, т.к. на тестовых HTTP стендах она не работает.
    // Пытаемся использовать современный clipboard API:
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      navigator.clipboard.writeText(text).then(() => {
        setSuccess("Адрес скопирован в буфер обмена");
        setTimeout(() => setSuccess(""), 2000);
      }).catch((err) => {
        console.error('clipboard.writeText failed, using fallback:', err);
        fallbackCopyTextToClipboard(text);
      });
    } else {
      fallbackCopyTextToClipboard(text);
    }
  };

  const fallbackCopyTextToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    // Скрываем textarea с экрана
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.width = "2em";
    textArea.style.height = "2em";
    textArea.style.padding = "0";
    textArea.style.border = "none";
    textArea.style.outline = "none";
    textArea.style.boxShadow = "none";
    textArea.style.background = "transparent";
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setSuccess("Адрес скопирован в буфер обмена");
        setTimeout(() => setSuccess(""), 2000);
      } else {
        setError("Не удалось скопировать. Скопируйте вручную.");
      }
    } catch (err) {
      console.error('Unable to copy to clipboard', err);
      setError("Не удалось скопировать. Скопируйте вручную.");
    }
    
    document.body.removeChild(textArea);
  };

  const checkReservedWallet = async () => {
    try {
      const response = await fetch(`${API_URL}/wallets/my-reserved`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data && data.address) {
          setReservedWallet(data);
          setTimeLeft(data.timeLeft);
          setDepositStep(2);
          // Восстанавливаем сумму из данных кошелька если она есть
          if (data.reservedAmount) {
            setDepositForm((prev) => ({
              ...prev,
              amount: data.reservedAmount.toString(),
            }));
          }
        }
      }
    } catch (error) {
      console.error("Ошибка при проверке кошелька:", error);
    }
  };

  const requestWallet = async () => {
    setIsRequestingWallet(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/wallets/reserve`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          amount: parseFloat(depositForm.amount),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setReservedWallet(data);
        setTimeLeft(data.timeLeft || 900); // 15 минут по умолчанию
        setDepositStep(2);
      } else {
        const error = await response.json();
        setError(error.message || "Ошибка при запросе кошелька");
      }
    } catch (error) {
      console.error("Ошибка при запросе кошелька:", error);
      setError("Ошибка при запросе кошелька");
    } finally {
      setIsRequestingWallet(false);
    }
  };

  const releaseWallet = async () => {
    if (!reservedWallet) return;

    try {
      const url = reservedWallet.walletId
        ? `${API_URL}/wallets/release/${reservedWallet.walletId}`
        : `${API_URL}/wallets/cancel-deposit/${reservedWallet.depositRequestId}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        setReservedWallet(null);
        setTimeLeft(0);
        setDepositStep(1);
        setShowCancelConfirm(false);
        setDepositForm({
          amount: "",
          method: "crypto",
          cardNumber: "",
          phoneNumber: "",
        });
      }
    } catch (error) {
      console.error("Ошибка при освобождении кошелька:", error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setDepositForm({ ...depositForm, [name]: value });
    setError("");
    setSuccess("");
  };

  const handleDepositSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (depositStep === 1) {
      // Первый шаг - валидация суммы
      if (!depositForm.amount) {
        setError("Пожалуйста, введите сумму");
        return;
      }

      const amount = parseFloat(depositForm.amount);
      if (isNaN(amount) || amount <= 0) {
        setError("Пожалуйста, введите корректную сумму");
        return;
      }

      if (amount < 1) {
        setError("Минимальная сумма пополнения: 1 USDT");
        return;
      }

      // Если выбран крипто метод, переходим к резервированию кошелька
      if (depositForm.method === "crypto") {
        await requestWallet();
      } else {
        // Для других методов - проверяем дополнительные поля
        if (depositForm.method === "sbp" && !depositForm.phoneNumber) {
          setError("Введите номер телефона для СБП");
          return;
        }

        if (depositForm.method === "bank_transfer" && !depositForm.cardNumber) {
          setError("Введите номер карты");
          return;
        }

        try {
          // Здесь будет API для создания заявки на пополнение
          setSuccess("Заявка на пополнение создана успешно!");
          setTimeout(() => {
            handleCloseDepositModal();
          }, 2000);
        } catch (err: any) {
          setError(
            err?.data?.message || "Произошла ошибка при создании заявки"
          );
        }
      }
    }
  };

  const handleCloseDepositModal = () => {
    setShowDepositModal(false);
    // НЕ сбрасываем состояние кошелька и шага, если кошелек зарезервирован
    if (!reservedWallet) {
      setDepositStep(1);
      setDepositForm({
        amount: "",
        method: "crypto",
        cardNumber: "",
        phoneNumber: "",
      });
    }
    setError("");
    setSuccess("");
  };

  return (
    <>
      <LayoutContainer>
        <Sidebar isOpen={!isMobile} isMobile={isMobile}>
          <SidebarContent>
            <LogoContainer>
              <Logo
                src={isDark ? "/VectorDark.svg" : "/Vector.svg"}
                alt="VangardPay Logo"
                onClick={() => {
                  if (formSidebarOpen) {
                    dispatch(closeFormSidebar());
                  }
                  if (location.pathname === "/dashboard") {
                    window.dispatchEvent(new CustomEvent("dashboard-refresh"));
                  }
                  navigate("/dashboard");
                }}
              />
            </LogoContainer>
            <NavigationContainer formSidebarOpen={formSidebarOpen}>
              <Navigation>
                {navItems.map((item) => (
                  <NavItem
                    key={item.path}
                    to={item.path}
                    onClick={(e) => {
                      if (item.path === "/dashboard" && location.pathname === "/dashboard") {
                        // Если мы уже на дашборде, предотвращаем навигацию и обновляем
                        e.preventDefault();
                        window.dispatchEvent(new CustomEvent("dashboard-refresh"));
                      }
                    }}
                    isActive={location.pathname === item.path}
                  >
                    <NavIcon isActive={location.pathname === item.path}>
                      <img src={location.pathname === item.path ? item.activeIcon : item.icon} alt={item.label} />
                    </NavIcon>
                    <NavLabel isActive={location.pathname === item.path}>{item.label}</NavLabel>
                    {item.badgeKey && badgeCounts[item.badgeKey] && (
                      <NavBadge isActive={location.pathname === item.path}>
                        {badgeCounts[item.badgeKey]}
                      </NavBadge>
                    )}
                  </NavItem>
                ))}
              </Navigation>
            </NavigationContainer>
          </SidebarContent>
        </Sidebar>

        <MainContent sidebarOpen={!isMobile} isMobile={isMobile}>
          <Header>
            <HeaderLeft>
              <MobileHeaderLogo
                src={isDark ? "/VectorDark.svg" : "/Vector.svg"}
                alt="VangardPay"
                onClick={() => navigate('/dashboard')}
              />
            </HeaderLeft>

            <HeaderRight>
              <ExchangeRateComponent exchangeRate={exchangeRate} />

              <OnlineStatusToggle
                onClick={handleToggleOnlineStatus}
                style={{
                  opacity: isTogglingStatus ? 0.6 : 1,
                  cursor: isTogglingStatus ? "wait" : "pointer",
                }}
              >
                <StatusIndicator
                  isActive={
                    (profile?.status || user?.trader?.status) === "ACTIVE"
                  }
                />
                <StatusText
                  isActive={
                    (profile?.status || user?.trader?.status) === "ACTIVE"
                  }
                >
                  {(profile?.status || user?.trader?.status) === "ACTIVE"
                    ? "Активен"
                    : "Неактивен"}
                </StatusText>
              </OnlineStatusToggle>

              <UserMenu>
                <UserAvatar>{getUserInitials()}</UserAvatar>
                <UserInfo>
                  <UserName>{user?.login}</UserName>
                  <UserRole>Трейдер</UserRole>
                </UserInfo>
              </UserMenu>

              <ThemeToggle />

              <LogoutButton onClick={handleLogout}>Выйти</LogoutButton>

              <HamburgerButton onClick={() => setMobileMenuOpen(true)}>
                ☰
              </HamburgerButton>
            </HeaderRight>
          </Header>

          <ContentArea>{children}</ContentArea>
        </MainContent>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNav>
          {navItems.map((item) => (
            <MobileNavItem
              key={item.path}
              to={item.path}
              isActive={location.pathname === item.path}
              onClick={(e) => {
                if (item.path === "/dashboard" && location.pathname === "/dashboard") {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent("dashboard-refresh"));
                }
              }}
            >
              <MobileNavIcon isActive={location.pathname === item.path}>
                <img src={location.pathname === item.path ? item.activeIcon : item.icon} alt={item.label} />
                {item.badgeKey && badgeCounts[item.badgeKey] && (
                  <MobileNavBadge>{badgeCounts[item.badgeKey]}</MobileNavBadge>
                )}
              </MobileNavIcon>
              <MobileNavLabel>{item.label}</MobileNavLabel>
            </MobileNavItem>
          ))}
        </MobileBottomNav>

        {/* Mobile Menu Drawer */}
        <MobileMenuOverlay isOpen={mobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />
        <MobileMenuDrawer isOpen={mobileMenuOpen}>
          <MobileMenuHeader>
            <MobileMenuUserInfo>
              <UserAvatar>{getUserInitials()}</UserAvatar>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem' }}>
                <UserName style={{ fontSize: '0.875rem' }}>{user?.login}</UserName>
                <UserRole>Трейдер</UserRole>
              </div>
            </MobileMenuUserInfo>
            <MobileMenuCloseBtn onClick={() => setMobileMenuOpen(false)}>×</MobileMenuCloseBtn>
          </MobileMenuHeader>

          <MobileMenuItem
            onClick={handleToggleOnlineStatus}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              opacity: isTogglingStatus ? 0.6 : 1,
              cursor: isTogglingStatus ? 'wait' : 'pointer',
            }}
          >
            <span>Статус</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <StatusIndicator
                isActive={(profile?.status || user?.trader?.status) === 'ACTIVE'}
              />
              <StatusText
                isActive={(profile?.status || user?.trader?.status) === 'ACTIVE'}
                style={{ flex: 'none', fontSize: '0.8125rem' }}
              >
                {(profile?.status || user?.trader?.status) === 'ACTIVE' ? 'Активен' : 'Неактивен'}
              </StatusText>
            </div>
          </MobileMenuItem>

          <MobileMenuDivider />

          <MobileMenuItem onClick={() => { setShowDepositModal(true); setMobileMenuOpen(false); }}>
            Пополнить баланс
          </MobileMenuItem>
          <MobileMenuItem onClick={() => { navigate('/balance'); setMobileMenuOpen(false); }}>
            Рабочий баланс: {balance?.balance?.toFixed(2) || '0.00'} USDT
          </MobileMenuItem>

          <MobileMenuDivider />

          <MobileMenuItem onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }}>
            Настройки
          </MobileMenuItem>

          <MobileMenuDivider />

          <MobileMenuItem variant="danger" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
            Выйти
          </MobileMenuItem>
        </MobileMenuDrawer>

        {/* Модальное окно пополнения баланса */}
        {showDepositModal && (
          <WithdrawModalOverlay>
            <WithdrawModalContent>
              <WithdrawModalHeader>
                <WithdrawModalTitle>Пополнение баланса</WithdrawModalTitle>
                <WithdrawCloseButton onClick={handleCloseDepositModal}>
                  ×
                </WithdrawCloseButton>
              </WithdrawModalHeader>

              {depositStep === 1 ? (
                <form onSubmit={handleDepositSubmit}>
                  <WithdrawFormGroup>
                    <WithdrawLabel htmlFor="amount">
                      Сумма пополнения (USDT)
                    </WithdrawLabel>
                    <WithdrawInput
                      type="number"
                      id="amount"
                      name="amount"
                      step="0.01"
                      min="1"
                      value={depositForm.amount}
                      onChange={handleInputChange}
                      placeholder="Введите сумму"
                    />
                  </WithdrawFormGroup>

                  <WithdrawFormGroup>
                    <WithdrawLabel htmlFor="method">
                      Способ пополнения
                    </WithdrawLabel>
                    <select
                      id="method"
                      name="method"
                      value={depositForm.method}
                      onChange={handleInputChange}
                      style={{
                        width: "100%",
                        padding: "0.75rem",
                        border: "1px solid var(--border-input)",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                        color: "var(--text-primary)",
                        background: "var(--bg-input)",
                      }}
                    >
                      <option value="crypto">Криптовалюта (USDT TRC-20)</option>
                    </select>
                  </WithdrawFormGroup>

                  {error && <WithdrawErrorText>{error}</WithdrawErrorText>}
                  {success && (
                    <WithdrawSuccessText>{success}</WithdrawSuccessText>
                  )}

                  <WithdrawButtonGroup>
                    <WithdrawFormButton
                      type="button"
                      variant="secondary"
                      onClick={handleCloseDepositModal}
                    >
                      Отмена
                    </WithdrawFormButton>
                    <WithdrawFormButton
                      type="submit"
                      variant="primary"
                      disabled={isRequestingWallet}
                    >
                      {isRequestingWallet ? "Запрос кошелька..." : "Далее"}
                    </WithdrawFormButton>
                  </WithdrawButtonGroup>
                </form>
              ) : (
                // Шаг 2 - Отображение зарезервированного кошелька
                <div>
                  {reservedWallet ? (
                    <WalletInfoCard isActive={true}>
                      <Alert type="info">
                        <strong>
                          Кошелёк зарезервирован на 30 минут
                        </strong>
                        <br />
                        Переведите {depositForm.amount} USDT (TRC-20) на адрес
                        ниже
                      </Alert>

                      <WithdrawLabel>Адрес для пополнения:</WithdrawLabel>
                      <WalletAddress
                        onClick={() => copyToClipboard(reservedWallet.address)}
                        title="Нажмите, чтобы скопировать"
                      >
                        {reservedWallet.address}
                      </WalletAddress>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginTop: "0.75rem",
                          fontSize: "0.875rem",
                          color: "var(--text-secondary)",
                        }}
                      >
                        <div>
                          <span style={{ color: 'var(--text-muted)' }}>Сеть:</span>{" "}
                          <strong style={{ color: 'var(--text-primary)' }}>TRON (TRC-20)</strong>
                          <span style={{ margin: '0 0.5rem', color: 'var(--border-color)' }}>|</span>
                          <span style={{ color: 'var(--text-muted)' }}>Сумма:</span>{" "}
                          <strong style={{ color: 'var(--text-primary)' }}>{depositForm.amount} USDT</strong>
                        </div>
                        <TimeLeft isUrgent={timeLeft < 300}>
                          {formatTime(timeLeft)}
                        </TimeLeft>
                      </div>

                      {error && <WithdrawErrorText>{error}</WithdrawErrorText>}
                      {success && (
                        <WithdrawSuccessText>{success}</WithdrawSuccessText>
                      )}

                      {showCancelConfirm ? (
                        <div style={{ marginTop: "1rem" }}>
                          <p style={{ marginBottom: "0.75rem", color: "var(--text-primary)", fontSize: "0.9375rem", textAlign: "center" }}>
                            Вы уверены, что хотите отменить бронирование?
                          </p>
                          <WithdrawButtonGroup>
                            <WithdrawFormButton
                              type="button"
                              variant="primary"
                              onClick={releaseWallet}
                            >
                              Да
                            </WithdrawFormButton>
                            <WithdrawFormButton
                              type="button"
                              variant="secondary"
                              onClick={() => setShowCancelConfirm(false)}
                            >
                              Нет
                            </WithdrawFormButton>
                          </WithdrawButtonGroup>
                        </div>
                      ) : (
                        <WithdrawButtonGroup>
                          <WithdrawFormButton
                            type="button"
                            variant="secondary"
                            onClick={() => setShowCancelConfirm(true)}
                          >
                            Отменить
                          </WithdrawFormButton>
                          <WithdrawFormButton
                            type="button"
                            variant="primary"
                            onClick={() =>
                              copyToClipboard(reservedWallet.address)
                            }
                            disabled={!!success}
                          >
                            {success ? "Скопировано!" : "Скопировать адрес"}
                          </WithdrawFormButton>
                        </WithdrawButtonGroup>
                      )}
                    </WalletInfoCard>
                  ) : (
                    <Alert type="error">
                      Не удалось зарезервировать кошелек. Попробуйте снова.
                    </Alert>
                  )}
                </div>
              )}
            </WithdrawModalContent>
          </WithdrawModalOverlay>
        )}
      </LayoutContainer>

      {/* Глобальная панель форм держателей и реквизитов - поверх всего */}
      {traderId && (
        <RequisitesSidebar
          isOpen={formSidebarOpen}
          onClose={() => dispatch(closeFormSidebar())}
          traderId={traderId}
          editingHolder={editingHolder}
          editingRequisite={editingRequisite}
        />
      )}
    </>
  );
}

export default Layout;
